import { ZoomTracker } from '@zoom/zdt_events_meetings';
import { InteractionEvent } from '@zoom/zdt_events_meetings/dist/events/meetings_v2/Interaction'
import { UserEntity } from '@zoom/zdt_events_meetings/dist/entities/meetings_v2/User';
import { DeviceEntity } from '@zoom/zdt_events_meetings/dist/entities/meetings_v2/Device';
import { ProductEntity } from '@zoom/zdt_events_meetings/dist/entities/meetings_v2/Product';

const EventNameEnum = {
  ai_virtual_background_displayed: 'ai_virtual_background_displayed',
  suggestion_selected: 'suggestion_selected',
  artistic_style_selected: 'artistic_style_selected',
  generate_button_clicked: 'generate_button_clicked',
  result_image_clicked: 'result_image_clicked',
}

const ProductTypeEnum = {
  meetings_v2: 'meetings_v2',
}

const EventSourceEnum = {
  client_settings: 'client_settings',
}

const FeatureNameEnum = {
  ai_virtual_background: 'ai_virtual_background',
}

const EventTypeEnum = {
  screen_displayed: 'screen_displayed',
  clicked: 'clicked',
}

const EventLocationEnum = {
  ai_virtual_background_main_page: 'ai_virtual_background_main_page',
  ai_virtual_background_result_page: 'ai_virtual_background_result_page',
}

let snowplowData = null;
let snowplowInitPromise = null;
let isSnowplowInitialized = false;

async function getSnowplowInfo() {
  if (!snowplowData) {
    snowplowData = await window.zoomSdk.callZoomApi('zmGetPAAPLogServiceInfo');
  }
  window.snowplowData = snowplowData;
  return snowplowData;
}

/**
 * Performs the actual Snowplow initialization
 * @returns {Promise<void>}
 */
async function performSnowplowInitialization() {
  try {
    const snowplow = await getSnowplowInfo();
    
    ZoomTracker.init({
      collector: {
        url: 'https://' + snowplow?.url
      },
      options: {
        dataCollectionFlag: snowplow?.toggle,
        credentials: 'omit',
        withCredentials: false,
      },
    });
    
    isSnowplowInitialized = true;
  } catch (error) {
    console.error('Snowplow initialization failed', error);
    // Reset promise on error so it can be retried
    snowplowInitPromise = null;
    throw error;
  }
}

export const initSnowplow = async () => {
  // If already initialized, return immediately
  if (isSnowplowInitialized) {
    return;
  }

  // If initialization is in progress, return the existing promise
  if (snowplowInitPromise) {
    return snowplowInitPromise;
  }

  // Start initialization and store the promise
  snowplowInitPromise = performSnowplowInitialization();
  return snowplowInitPromise;
};

// Helper function to ensure Snowplow is initialized before logging
async function ensureSnowplowInitialized() {
  // If already initialized, return immediately
  if (isSnowplowInitialized) {
    return;
  }

  // If initialization is in progress, wait for it
  if (snowplowInitPromise) {
    await snowplowInitPromise;
    return;
  }
}

/**
 * InteractionEvent
 * @param {*} eventName // EVENT_NAME
 * @param {*} eventType // EVENT_TYPE
 * @param {*} eventLocation // EVENT_LOCATION
 * @param {*} errorReason // ERROR_REASON
 */
async function snowplow(eventName, eventType, eventLocation, errorReason = '') {
  if (!eventName || !eventType || !eventLocation) {
    console.error('Empty event parameters');
    return;
  }

  // Wait for Snowplow to be initialized before logging
  await ensureSnowplowInitialized();

  // If still not initialized after waiting, skip logging
  if (!isSnowplowInitialized) {
    console.warn('Snowplow not initialized, skipping event:', eventName);
    return;
  }

  // Create entities with snowplow data
  const productEntity = new ProductEntity();
  productEntity.clientType = window.snowplowData?.client_type ?? window.platform;
  productEntity.clientVersion = window.snowplowData?.client_version ?? '';
  productEntity.clusterDbName = window.snowplowData?.cluster_db_name ?? '';

  const userEntity = new UserEntity();
  userEntity.userId = window.snowplowData?.user_id ?? '';
  userEntity.accountId = window.snowplowData?.account_id ?? '';

  const deviceEntity = new DeviceEntity();
  deviceEntity.deviceId = window.snowplowData?.device_id ?? '';
  deviceEntity.deviceModel = window.snowplowData?.device_model ?? '';
  deviceEntity.osName = window.snowplowData?.os_name ?? 'mac';
  deviceEntity.osVersion = window.snowplowData?.os_version ?? '';

  // Create and configure the event object
  const eventObject = new InteractionEvent();
  eventObject.featureName = FeatureNameEnum.ai_virtual_background;
  eventObject.productType = ProductTypeEnum.meetings_v2;
  eventObject.eventType = eventType;
  eventObject.eventLocation = eventLocation;
  eventObject.eventName = eventName;
  eventObject.eventSource = EventSourceEnum.client_settings;
  eventObject.productEntity = productEntity;
  eventObject.userEntity = userEntity;
  eventObject.deviceEntity = deviceEntity;
  eventObject.errorReason = errorReason;

  ZoomTracker.track([eventObject]);
}

export const logAIVirtualBackgroundDisplayed = async ({
  errorReason = '',
} = {}) => {
  await snowplow(
    EventNameEnum.ai_virtual_background_displayed,
    EventTypeEnum.screen_displayed,
    EventLocationEnum.ai_virtual_background_main_page,
    errorReason,
  )
}

export const logSuggestionSelected = async ({ errorReason = '' } = {}) => {
  await snowplow(
    EventNameEnum.suggestion_selected,
    EventTypeEnum.clicked,
    EventLocationEnum.ai_virtual_background_main_page,
    errorReason,
  )
}

export const logArtisticStyleSelected = async ({ errorReason = '' } = {}) => {
  await snowplow(
    EventNameEnum.artistic_style_selected,
    EventTypeEnum.clicked,
    EventLocationEnum.ai_virtual_background_main_page,
    errorReason
  )
}

export const logGenerateButtonClicked = async ({ errorReason = '' } = {}) => {
  await snowplow(
    EventNameEnum.generate_button_clicked,
    EventTypeEnum.clicked,
    EventLocationEnum.ai_virtual_background_main_page,
    errorReason,
  )
}

export const logResultImageClicked = async ({ errorReason = '' } = {}) => {
  await snowplow(
    EventNameEnum.result_image_clicked,
    EventTypeEnum.clicked,
    EventLocationEnum.ai_virtual_background_result_page,
    errorReason,
  )
}